import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Globe, Phone, Mail, User, MessageSquare, ArrowRight,
  CheckCircle2, ShieldCheck, Lock,
} from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const ORG_TYPES = [
  { value: "insurance", label: "Insurance company" },
  { value: "nonprofit", label: "Non-profit / rescue" },
  { value: "employer", label: "Employer / benefits group" },
  { value: "other", label: "Other" },
];

const VOLUMES = ["1 to 50", "50 to 250", "250 to 1,000", "1,000+"];

export default function PartnerApplyPage() {
  const [form, setForm] = useState({
    org_type: "insurance",
    org_name: "",
    website: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    address_parts: null,
    est_members: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_name || !form.contact_name || !form.email) {
      toast.error("Please fill in your organization name, contact, and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/partner-applications`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl border border-paw-border bg-white text-paw-text placeholder:text-paw-text-muted focus:outline-none focus:ring-2 focus:ring-paw-primary/30 focus:border-paw-primary transition";
  const iconInputCls =
    "w-full pl-10 pr-4 py-3 rounded-xl border border-paw-border bg-white text-paw-text placeholder:text-paw-text-muted focus:outline-none focus:ring-2 focus:ring-paw-primary/30 focus:border-paw-primary transition";

  if (submitted) {
    return (
      <div className="bg-paw-cream min-h-[70vh] grid place-items-center px-6 py-20">
        <div className="max-w-lg w-full bg-white rounded-[28px] border border-paw-border shadow-sm p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-paw-blue-50 grid place-items-center mx-auto mb-5"><CheckCircle2 className="w-8 h-8 text-paw-primary" /></div>
          <h1 className="text-2xl font-extrabold tracking-tight text-paw-navy">Application received</h1>
          <p className="mt-3 text-paw-text-secondary">Thanks, {form.contact_name.split(" ")[0] || "there"}. Our partnerships team will review your details and reach out to {form.email} to set up your partner account.</p>
          <Link to="/for-insurance" className="mt-7 inline-flex items-center justify-center gap-2 bg-paw-primary hover:bg-paw-primary-hover text-white rounded-full px-7 py-3.5 font-semibold transition-all">
            Back to partners <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paw-cream">
      <section className="bg-white pt-14 pb-10">
        <div className="max-w-[760px] mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">
            <Building2 className="w-4 h-4" /> Partner application
          </span>
          <h1 className="mt-3 text-3xl sm:text-[42px] font-extrabold tracking-tight text-paw-navy leading-tight">Become a PawLinx partner</h1>
          <p className="mt-4 text-lg text-paw-text-secondary">Tell us about your organization. Our partnerships team will set you up with a secure portal and an agreed medication rate.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-paw-text-secondary">
            <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-paw-primary" /> Encrypted submission</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-paw-primary" /> No obligation</span>
          </div>
        </div>
      </section>

      <section className="py-12">
        <form onSubmit={submit} className="max-w-[760px] mx-auto px-6">
          <div className="bg-white rounded-[28px] border border-paw-border shadow-sm p-6 sm:p-10 space-y-6">
            {/* Org type */}
            <div>
              <label className="block text-sm font-semibold text-paw-navy mb-2">Organization type</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ORG_TYPES.map((t) => (
                  <button type="button" key={t.value} onClick={() => set("org_type", t.value)}
                    className={`px-3 py-2.5 rounded-xl text-sm font-medium border transition ${form.org_type === t.value ? "border-paw-primary bg-paw-blue-50 text-paw-primary" : "border-paw-border bg-white text-paw-text-secondary hover:border-paw-text-muted"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-paw-navy mb-2">Organization name</label>
                <div className="relative">
                  <Building2 className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input className={iconInputCls} value={form.org_name} onChange={(e) => set("org_name", e.target.value)} placeholder="e.g. Northwind Pet Insurance" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-paw-navy mb-2">Website</label>
                <div className="relative">
                  <Globe className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input className={iconInputCls} value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" />
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-paw-navy mb-2">Contact name</label>
                <div className="relative">
                  <User className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input className={iconInputCls} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Full name" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-paw-navy mb-2">Work email</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input type="email" className={iconInputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" required />
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-paw-navy mb-2">Phone</label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input className={iconInputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 555 5555" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-paw-navy mb-2">Estimated members</label>
                <select className={inputCls} value={form.est_members} onChange={(e) => set("est_members", e.target.value)}>
                  <option value="">Select a range</option>
                  {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-paw-navy mb-2">Organization address</label>
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => set("address", v)}
                onSelect={(parts) => setForm((p) => ({ ...p, address: parts.formatted, address_parts: parts }))}
                placeholder="Start typing your address"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-paw-navy mb-2">Anything else we should know?</label>
              <div className="relative">
                <MessageSquare className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-3.5 pointer-events-none" />
                <textarea rows={4} className={`${iconInputCls} resize-none`} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your members and what you're looking for." />
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-60 text-white rounded-full px-7 py-3.5 font-semibold transition-all shadow-[0_8px_20px_-8px_rgba(15,98,214,0.6)]">
              {loading ? "Submitting..." : <>Submit application <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-xs text-paw-text-muted text-center">By submitting, you agree to be contacted about a PawLinx partnership. Your details are encrypted and never shared.</p>
          </div>
        </form>
      </section>
    </div>
  );
}
